import React from 'react';
import { ModalController } from '../../../components/Modal/ControlledModal';
import { RequestAgreement, addAgreementToContract } from '../../../model/contract';
import { Form, useChanges } from '../../../components/Form/Form';
import { TextField } from '../../../components/Form/TextField/TextField';
import { AsyncSearch } from '../../../components/Form/AsyncSearch/AsyncSearch';
import { Switcher } from '../../../components/Form/Switcher/Switcher';
import { Label } from '../../../components/Form/Label/Label';
import { Select } from '../../../components/Form/Select/Select';
import { Icon } from '../../../components/Icon/Icon';
import { Button } from '../../../components/Button/Button';
import { handleError } from '../../../model/notification';
import { Checkbox } from '../../../components/Form/Checkbox/Checkbox';
import { t } from '../../../locales/translate';
import { presence } from '../../../util/util';
import { Stack } from '../../../components/Stack/Stack';
import { Spacer } from '../../../components/Spacer/Spacer';
import { Grid } from '../../../components/Grid/Grid';

interface Props {
  controller: ModalController<RequestAgreement>;
  contractId: string;
}

export function AddAgreement({ controller, contractId }: Props): JSX.Element {
  const defaultValues = {
    name: '',
    allshops: true,
    shopIds: [],
    contractAgreementLines: {
      0: {
        companyId: '',
        postTax: false,
        vatOverride: null,
        fee: false,
        amount: 0,
        contractType: '',
        description: '',
      },
    },
  };

  const [changes, setChanges, model, trackChanges] = useChanges<RequestAgreement & { allshops: boolean }>({
    ...defaultValues,
    ...controller.model,
  });

  async function handleSubmit(): Promise<void> {
    try {
      await addAgreementToContract(contractId, {
        name: model.name,
        shopIds: model.allshops ? [] : model.shopIds,
        contractAgreementLines: Object.values(model.contractAgreementLines).map((al) => ({
          ...al,
          amount: al.contractType === 'percentage' ? al.amount / 100 : al.amount,
          vatOverride: al.vatOverride ? al.vatOverride / 100 : null,
          description: presence(al.description),
        })),
      });
      controller.close();
    } catch (e) {
      handleError(e);
    }
  }

  function removeContractLine(key: string): void {
    const newModel = { ...model };
    delete newModel.contractAgreementLines[key];
    setChanges(newModel);
  }

  function addContractLine(): void {
    setChanges({
      ...changes,
      contractAgreementLines: {
        ...model.contractAgreementLines,
        [Object.values(model.contractAgreementLines).length]: {
          companyId: '',
          postTax: false,
          vatOverride: null,
          fee: false,
          amount: 0,
          contractType: 'percentage',
          description: '',
        },
      },
    });
  }

  function checkContractLines(): boolean {
    if (changes.contractAgreementLines) {
      const lines = Object.values(model.contractAgreementLines)
        .filter(Boolean)
        .map((i) => !!i.companyId);
      if (lines.length > 0) {
        return lines.reduce((a, b) => a && b);
      }
    }
    return false;
  }

  function isPercentage(key: string): boolean {
    return model.contractAgreementLines[key].contractType === 'percentage';
  }

  return (
    <Form model={model} onInput={trackChanges}>
      {(form) => {
        controller.configure({
          canSubmit: form.valid && checkContractLines(),
          onSubmit: handleSubmit,
        });
        return (
          <Stack>
            <TextField name="name" label={t.model.agreement.title} required />
            <Spacer size="none" />
            <Checkbox name="allshops" label={`${t.general.all} ${t.model.shop._plural}`} />
            {!model.allshops && <AsyncSearch name="shopIds" label={t.model.shop._plural} searchIn={['shop']} />}
            {Object.keys(model.contractAgreementLines).map((key) => (
              <>
                <Spacer size="none" />
                <Label
                  title={`${t.model.contract.contractLine} ${parseInt(key, 10) + 1}`}
                  actions={<Icon name="trash" onClick={() => removeContractLine(key)} />}
                />
                <AsyncSearch
                  name={`contractAgreementLines.${key}.companyId`}
                  label={t.model.agreement.partner}
                  searchIn={['company']}
                  required
                />
                <Grid>
                  <Switcher
                    onChange={(v) => {
                      setChanges({
                        ...changes,
                        contractAgreementLines: {
                          ...model.contractAgreementLines,
                          [key]: {
                            ...model.contractAgreementLines[key],
                            postTax: v,
                          },
                        },
                      });
                    }}
                    labels={[t.model.price.gross, t.model.price.net]}
                    value={model.contractAgreementLines[key] && model.contractAgreementLines[key].postTax}
                    label={t.model.agreement.postTax}
                  />
                  <TextField
                    name={`contractAgreementLines.${key}.vatOverride`}
                    label={t.model.agreement.vatOverride}
                    type="number"
                    min={0}
                    max={100}
                    required
                  />
                </Grid>
                <Grid>
                  <Select
                    name={`contractAgreementLines.${key}.contractType`}
                    label={t.model.agreement.type}
                    defaultValue="percentage"
                    required
                    options={[
                      { value: 'percentage', label: t.general.percentage },
                      { value: 'absolute', label: t.general.absolute },
                    ]}
                  />
                  <TextField
                    name={`contractAgreementLines.${key}.amount`}
                    label={isPercentage(key) ? t.general.percent : 'EUR'}
                    type="number"
                    required
                  />
                </Grid>
                <TextField name={`contractAgreementLines.${key}.description`} label={t.general.description} />
              </>
            ))}
            <div>
              <Button
                title={t.actions.add(t.model.contract.contractLine)}
                type="secondary"
                onClick={() => addContractLine()}
              />
            </div>
          </Stack>
        );
      }}
    </Form>
  );
}
